import React from 'react';
import { toLocalYMD } from '../lib/dates';
import { Check, SkipForward } from 'lucide-react';

// Bar rules (all display modes): done = barColor (default green), skipped = yellow, overdue = red.
const SKIP_YELLOW = '#facc15';
const OVERDUE_RED = '#ef4444';

const isLightColor = (hex) => {
  const c = (hex || '').replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.substr(0, 2), 16), g = parseInt(c.substr(2, 2), 16), b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
};

// Chart-style week strip: bar chart (job counts) above slim day chips.
export const ChartWeekStrip = ({ weekDates, jobsPerDay, dayProgressStats, selectedDayIndex, onDayTap, bankHolidays = [], dayNotes = {}, barColor = '#10b981' }) => {
  const max = Math.max(...weekDates.map((_, i) => jobsPerDay[i] || 0), 1);
  return (
    <div className="w-full" data-testid="chart-week-strip">
      <div className="flex gap-1.5 items-end h-16 mb-1.5 px-0.5">
        {weekDates.map((date, i) => {
          const count = jobsPerDay[i] || 0;
          // Empty days: completely blank bar area — no stub pill.
          if (count === 0) return <div key={i} className="flex-1 min-w-0 h-full" data-testid={`chart-bar-${i}`} />;
          const sel = i === selectedDayIndex;
          const stats = dayProgressStats[i] || {};
          const pct = (n) => (stats.total > 0 ? Math.min(100, (n / stats.total) * 100) : 0);
          const donePct = pct(stats.completed || 0);
          const skipPct = pct(stats.skipped || 0);
          const overduePct = pct(stats.overdue || 0);
          const h = Math.max(14, (count / max) * 100);
          return (
            <button
              key={i}
              onClick={() => onDayTap(i, date)}
              className="flex-1 min-w-0 h-full flex flex-col items-center justify-end gap-0.5"
              data-testid={`chart-bar-${i}`}
            >
              <span className={`text-[9px] font-bold leading-none ${sel ? 'text-white' : 'text-white/70'}`}>{count}</span>
              <div
                className={`w-full rounded-md overflow-hidden flex flex-col justify-end transition-all duration-300 ${sel ? 'ring-1 ring-white/60' : ''}`}
                style={{ height: `${h}%`, background: sel ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.24)' }}
              >
                {donePct > 0 && <div className="w-full transition-all duration-500" style={{ height: `${donePct}%`, background: barColor }} />}
                {skipPct > 0 && <div className="w-full transition-all duration-500" style={{ height: `${skipPct}%`, background: SKIP_YELLOW }} />}
                {overduePct > 0 && <div className="w-full transition-all duration-500" style={{ height: `${overduePct}%`, background: OVERDUE_RED }} />}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex gap-1.5">
        {weekDates.map((date, i) => {
          const sel = i === selectedDayIndex;
          const dateStr = toLocalYMD(date);
          const isBankHoliday = bankHolidays.some(hh => hh.date === dateStr);
          const hasNotes = (dayNotes[dateStr] || []).length > 0;
          const selText = isLightColor(barColor) ? '#0f172a' : '#ffffff';
          return (
            <button
              key={i}
              onClick={() => onDayTap(i, date)}
              data-testid={`chart-day-${i}`}
              className={`relative flex-1 min-w-0 py-1.5 rounded-lg text-center transition-all ${
                sel ? '' : isBankHoliday ? 'bg-red-900/40' : 'hover:bg-white/5'
              }`}
              style={sel ? { backgroundColor: barColor } : {}}
            >
              <span
                className="block text-[9px] font-bold tracking-wide"
                style={{ color: sel ? selText : isBankHoliday ? '#fca5a5' : 'rgba(255,255,255,0.5)' }}
              >
                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'][i]}
              </span>
              <span
                className="block text-sm font-extrabold leading-tight"
                style={{ color: sel ? selText : 'rgba(255,255,255,0.9)' }}
              >
                {date.getDate()}
              </span>
              {hasNotes && <span className="absolute top-0.5 right-1 w-1.5 h-1.5 bg-yellow-400 rounded-full" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// One slim strip replacing the four stat tiles: "3/17 done ▓▓░░ £406.50 [Select]"
export const SlimProgressStrip = ({ jobs, hidePrice = false, isWorker = false, selectedCount = 0, onSelectAll, overdueCount = 0, barColor = '#10b981' }) => {
  const doneCount = jobs.filter(j => j.status === 'done' || j.status === 'completed').length;
  const skippedCount = jobs.filter(j => j.status === 'skipped').length;
  const active = jobs.length - skippedCount;
  const totalValue = jobs.filter(j => j.status !== 'skipped').reduce((s, j) => s + (j.price || 0), 0);
  const allSelected = selectedCount === jobs.length && jobs.length > 0;
  const w = (n) => (jobs.length > 0 ? (n / jobs.length) * 100 : 0);
  return (
    <div className="mx-3 mt-2 mb-1 bg-white rounded-xl border border-slate-100 shadow-sm px-3 py-2.5 flex items-center gap-3" data-testid="slim-progress-strip">
      <span className="text-sm font-bold text-slate-900 whitespace-nowrap" data-testid="stats-jobs-count">{doneCount}/{active} done</span>
      <div className="flex-1 h-2 bg-zinc-200 rounded-full overflow-hidden flex">
        <div className="h-full transition-all duration-500" style={{ width: `${w(doneCount)}%`, background: barColor }} />
        <div className="h-full transition-all duration-500" style={{ width: `${w(skippedCount)}%`, background: SKIP_YELLOW }} />
        <div className="h-full transition-all duration-500" style={{ width: `${w(overdueCount)}%`, background: OVERDUE_RED }} />
      </div>
      {overdueCount > 0 && <span className="text-[11px] font-bold text-red-500 whitespace-nowrap">{overdueCount} late</span>}
      {skippedCount > 0 && <span className="text-[11px] font-bold text-yellow-600 whitespace-nowrap">{skippedCount} skip</span>}
      {!hidePrice && <span className="text-sm font-bold text-slate-900 whitespace-nowrap">£{totalValue.toFixed(2)}</span>}
      {!isWorker && onSelectAll && (
        <button
          onClick={onSelectAll}
          className={`px-2 h-6 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all whitespace-nowrap ${
            allSelected ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
          }`}
          data-testid="select-all-summary-btn"
        >
          {allSelected ? '✓ All' : 'Select'}
        </button>
      )}
    </div>
  );
};

// Dense address-first job row. Tick = quick Done, tap row = full detail panel.
export const CompactJobRow = ({ job, index = 0, currentStatus, onClick, onComplete, canSeePrice = true, getZoneColor, isOverdue = false }) => {
  const status = currentStatus?.status || job.status;
  const done = status === 'done' || status === 'completed';
  const skipped = status === 'skipped';
  const addr = (job.address || '').split(',')[0].trim() || job.customerName || '';
  const zone = job.zone || '';
  const zoneColor = zone && getZoneColor ? getZoneColor(zone) : '#94a3b8';
  return (
    <div
      onClick={onClick}
      data-testid={`compact-job-row-${job.id}`}
      className={`mx-3 mb-1.5 rounded-xl border shadow-sm px-3 py-2.5 flex items-center gap-2.5 cursor-pointer active:scale-[0.99] transition-all ${
        done ? 'bg-emerald-50/60 border-emerald-100' : skipped ? 'bg-yellow-50/70 border-yellow-200' : isOverdue ? 'bg-white border-red-200' : 'bg-white border-slate-100'
      }`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); if (!done && !skipped) onComplete(job); }}
        data-testid={`compact-done-btn-${job.id}`}
        className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          done
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : skipped
            ? 'bg-yellow-100 border-yellow-300'
            : 'bg-white border-zinc-300 text-zinc-200 active:border-emerald-400 active:text-emerald-400'
        }`}
        aria-label={done ? 'Completed' : skipped ? 'Skipped' : 'Mark done'}
      >
        {skipped ? <SkipForward size={14} className="text-yellow-600" /> : <Check size={16} strokeWidth={3} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-[13.5px] font-semibold truncate ${done ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>
          <span className="text-zinc-400 font-bold mr-1">{index + 1}.</span>{addr}
        </p>
        <p className="text-[11.5px] text-zinc-500 truncate">
          {job.customerName || ''}{job.assignee ? ` · ${job.assignee}` : ''}{job.services ? ` · ${job.services}` : ''}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        {canSeePrice && <p className={`text-sm font-extrabold ${done ? 'text-zinc-400' : 'text-zinc-900'}`}>£{(job.price || 0).toFixed(2)}</p>}
        {zone && (
          <span
            className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 max-w-[90px] truncate"
            style={{ backgroundColor: `${zoneColor}1a`, color: zoneColor }}
          >
            {zone}
          </span>
        )}
      </div>
    </div>
  );
};
